<script lang="ts" generics="OPTION extends string | number">
    import {KpPopoverPanel} from 'shared/ui-widgets/popover';
    import type {ButtonStyle, ComponentSize} from '../types';
    import {createEventDispatcher, onMount, tick} from 'svelte';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {Listbox, ListboxButton, ListboxOptions, ListboxOption} from '@rgossiaux/svelte-headlessui';
    import {uuid as generateUuid} from 'shared/utils/custom-utils';

    export let id: string = generateUuid();
    export let options: (OPTION)[] = [];
    export let value: OPTION;
    export let disabled = false;
    export let use = [];
    export let size: ComponentSize = 'md';
    export let additionalButtonClass = '';
    export let additionalContainerClass = '';
    export let buttonStyle: ButtonStyle = 'default';
    export let isOptionDisabled: (option: string | number) => boolean = () => false;

    const dispatch = createEventDispatcher<{ change: OPTION }>();
    const [floatingRef, floatingContent, update] = createFloatingActions(floatingUiCommonSettings('bottom-start'));

    $: buttonId = `${id}-dropdown-menu-wrapper-button`;
    $: panelId = `${id}-dropdown-menu-wrapper-panel`;

    let currentValue = value;
    let lastValue = value; // to distinguish between change from `value` prop (top-down) and change from `currentValue` prop binding (bottom-up)
    $: {
        if (value !== currentValue && value !== lastValue) {
            onValuePropertyChange();
        }
        if (currentValue !== value && currentValue !== lastValue) {
            onCurrentValueBindingChange();
        }
    }

    onMount(async () => {
        await tick();
        update();
    });

    function onValuePropertyChange() {
        currentValue = value;
        lastValue = value;
    }

    function onCurrentValueBindingChange() {
        value = currentValue;
        lastValue = currentValue;
        dispatch('change', currentValue);
    }
</script>

<Listbox {id}
         class="kp-dropdown-listbox {additionalContainerClass}"
         use={[floatingRef]}
         bind:value={currentValue}
         on:click={() => update()}
         {disabled}
         {...$$restProps}
         let:open>

    <slot name="label"/>

    <ListboxButton id={buttonId}
                   type="button"
                   role="combobox"
                   value={currentValue}
                   class="kp-dropdown-menu-toggle input-{size} btn btn-{buttonStyle} {open ? 'active': ''} {additionalButtonClass}"
                   {use}>

        <slot name="button"/>
    </ListboxButton>

    <KpPopoverPanel {open}
                    popoverMode="manual"
                    id={panelId}
                    use={[floatingContent]}>

        <ListboxOptions static class="kp-listbox-dropdown-menu">
            {#each options as option}
                <ListboxOption value={option}
                               disabled={disabled || isOptionDisabled(option)}
                               class="{({active, selected}) => `kp-listbox-dropdown-option ${active ? 'active' : ''} ${selected ? 'selected' : ''} ${disabled || isOptionDisabled(option) ? 'disabled' : ''}`}">

                    <slot name="option" {option}/>
                </ListboxOption>
            {/each}
        </ListboxOptions>
    </KpPopoverPanel>
</Listbox>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    :global {
        .kp-dropdown-listbox {
            .kp-dropdown-menu-toggle {
                display: flex;
                align-items: center;
            }

            .kp-popover-panel {
                --popover-content-padding: @spacing-s;
            }

            .kp-listbox-dropdown-menu {
                display: flex;
                flex-direction: column;
                min-width: 200px;
                max-height: 400px;
                overflow-y: auto;

                .kp-listbox-dropdown-option {
                    display: flex;
                    align-items: center;
                    text-align: start;
                    font-size: @font-size-sm;
                    padding: @spacing-xs @spacing-m;
                    gap: @spacing-m;
                    font-weight: normal;
                    border-radius: @border-radius-default;
                    color: var(--text-default);
                    white-space: nowrap;
                    text-decoration: none;
                    cursor: pointer;
                    transition: background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;

                    &.disabled {
                        opacity: 0.5;
                        background-color: @themed-disabled-bg;
                        cursor: not-allowed;
                    }

                    &.active {
                        background-color: @themed-body-bg-blue-highlighted;
                    }

                    &.selected {
                        background-color: var(--accent-blue);
                        color: white;
                    }
                }
            }
        }
    }
</style>